/**
 * Working Days Configuration
 *
 * Defines location-specific schedules and holiday rules for calculating
 * working days in the dealership forecasting system.
 */

// ============================================
// TYPE DEFINITIONS
// ============================================

/** Day of week (0 = Sunday, 6 = Saturday) */
export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** Department types relevant to working days scheduling */
export type SchedulableDepartment =
  | 'NEW_VEHICLE'
  | 'USED_VEHICLE'
  | 'SERVICE'
  | 'PARTS'
  | 'BODY_SHOP'
  | 'RENTAL'
  | 'OTHERS';

/** Closure status for a department */
export type ClosureStatus = 'OPEN' | 'CLOSED';

/** Holiday closure type */
export type HolidayClosureType = 'ALL' | 'PARTIAL_SALES_ONLY';

/** Holiday names used in the system */
export type HolidayName =
  | 'MEMORIAL_DAY'
  | 'JULY_4TH'
  | 'LABOR_DAY'
  | 'THANKSGIVING'
  | 'CHRISTMAS'
  | 'NEW_YEARS_DAY';

/** Department schedule for a specific day type */
export interface DepartmentSchedule {
  NEW_VEHICLE: ClosureStatus;
  USED_VEHICLE: ClosureStatus;
  SERVICE: ClosureStatus;
  PARTS: ClosureStatus;
  BODY_SHOP: ClosureStatus;
  RENTAL: ClosureStatus;
  OTHERS: ClosureStatus;
}

/** Legacy Saturday schedule type (for backward compatibility) */
export type SaturdayScheduleType = 'ALL_OPEN' | 'SALES_ONLY' | 'ALL_CLOSED';

/** Per-department Saturday schedule */
export interface SaturdayDepartmentSchedule {
  NEW_VEHICLE: boolean;    // true = open on Saturday
  USED_VEHICLE: boolean;
  SERVICE: boolean;
  PARTS: boolean;
  BODY_SHOP: boolean;
  RENTAL: boolean;
}

/** Departments configurable for Saturday (display order) */
export const CONFIGURABLE_SATURDAY_DEPARTMENTS: (keyof SaturdayDepartmentSchedule)[] = [
  'NEW_VEHICLE',
  'USED_VEHICLE',
  'SERVICE',
  'PARTS',
  'BODY_SHOP',
  'RENTAL',
];

/** Display names for departments */
export const DEPARTMENT_DISPLAY_NAMES: Record<keyof SaturdayDepartmentSchedule, string> = {
  NEW_VEHICLE: 'New',
  USED_VEHICLE: 'Used',
  SERVICE: 'Service',
  PARTS: 'Parts',
  BODY_SHOP: 'Body Shop',
  RENTAL: 'Rental',
};

/** Default Saturday schedule - all departments open */
export const DEFAULT_SATURDAY_SCHEDULE: SaturdayDepartmentSchedule = {
  NEW_VEHICLE: true,
  USED_VEHICLE: true,
  SERVICE: true,
  PARTS: true,
  BODY_SHOP: true,
  RENTAL: true,
};

/** Location schedule configuration */
export interface LocationScheduleConfig {
  locationId: number;
  locationName: string;
  saturdaySchedule: SaturdayDepartmentSchedule;
}

/** Holiday definition */
export interface HolidayDefinition {
  name: HolidayName;
  displayName: string;
  getDate: (year: number) => Date;
  closureType: HolidayClosureType;
}

/** Resolved holiday for a specific year */
export interface ResolvedHoliday {
  name: HolidayName;
  displayName: string;
  date: Date;
  closureType: HolidayClosureType;
}

// ============================================
// SCHEDULE TEMPLATES
// ============================================

/** All departments open */
const ALL_OPEN: DepartmentSchedule = {
  NEW_VEHICLE: 'OPEN',
  USED_VEHICLE: 'OPEN',
  SERVICE: 'OPEN',
  PARTS: 'OPEN',
  BODY_SHOP: 'OPEN',
  RENTAL: 'OPEN',
  OTHERS: 'OPEN',
};

/** Only sales departments open (New/Used) */
const SALES_ONLY: DepartmentSchedule = {
  NEW_VEHICLE: 'OPEN',
  USED_VEHICLE: 'OPEN',
  SERVICE: 'CLOSED',
  PARTS: 'CLOSED',
  BODY_SHOP: 'CLOSED',
  RENTAL: 'CLOSED',
  OTHERS: 'CLOSED',
};

/** All departments closed */
const ALL_CLOSED: DepartmentSchedule = {
  NEW_VEHICLE: 'CLOSED',
  USED_VEHICLE: 'CLOSED',
  SERVICE: 'CLOSED',
  PARTS: 'CLOSED',
  BODY_SHOP: 'CLOSED',
  RENTAL: 'CLOSED',
  OTHERS: 'CLOSED',
};

/** Map Saturday schedule type to department schedule */
export const SATURDAY_SCHEDULES: Record<SaturdayScheduleType, DepartmentSchedule> = {
  ALL_OPEN: ALL_OPEN,
  SALES_ONLY: SALES_ONLY,
  ALL_CLOSED: ALL_CLOSED,
};

// ============================================
// LOCATION CONFIGURATIONS
// ============================================

/** Convert legacy saturdayType to new saturdaySchedule format */
function convertLegacySaturdayType(saturdayType: SaturdayScheduleType): SaturdayDepartmentSchedule {
  switch (saturdayType) {
    case 'ALL_OPEN':
      return { ...DEFAULT_SATURDAY_SCHEDULE };
    case 'SALES_ONLY':
      return {
        NEW_VEHICLE: true,
        USED_VEHICLE: true,
        SERVICE: false,
        PARTS: false,
        BODY_SHOP: false,
        RENTAL: false,
      };
    case 'ALL_CLOSED':
      return {
        NEW_VEHICLE: false,
        USED_VEHICLE: false,
        SERVICE: false,
        PARTS: false,
        BODY_SHOP: false,
        RENTAL: false,
      };
    default:
      return { ...DEFAULT_SATURDAY_SCHEDULE };
  }
}

/**
 * Location-specific Saturday schedules (defaults)
 *
 * All locations are:
 * - CLOSED on Sunday
 * - OPEN Monday through Friday (except holidays)
 * - Saturday varies by location as defined below
 */
export const LOCATION_SCHEDULES: LocationScheduleConfig[] = [
  { locationId: 1, locationName: "Darling's Ford VW Audi", saturdaySchedule: convertLegacySaturdayType('ALL_OPEN') },
  { locationId: 2, locationName: "Darling's Honda Nissan Volvo", saturdaySchedule: convertLegacySaturdayType('ALL_OPEN') },
  { locationId: 3, locationName: "Darling's Auto Mall", saturdaySchedule: convertLegacySaturdayType('ALL_OPEN') },
  { locationId: 4, locationName: "Darling's Chrysler Dodge Hyundai", saturdaySchedule: convertLegacySaturdayType('ALL_OPEN') },
  { locationId: 5, locationName: "Darlings Chevrolet", saturdaySchedule: convertLegacySaturdayType('SALES_ONLY') },
  { locationId: 10, locationName: "Brunswick Ford", saturdaySchedule: convertLegacySaturdayType('ALL_OPEN') },
  { locationId: 11, locationName: "Darlings CDJR Newport", saturdaySchedule: convertLegacySaturdayType('SALES_ONLY') },
  { locationId: 12, locationName: "Darling's Green Point", saturdaySchedule: convertLegacySaturdayType('ALL_CLOSED') },
  { locationId: 14, locationName: "Darling's Kia", saturdaySchedule: convertLegacySaturdayType('ALL_OPEN') },
  { locationId: 44, locationName: "Darling's Undercoating & More", saturdaySchedule: convertLegacySaturdayType('ALL_CLOSED') },
];

// ============================================
// HOLIDAY DATE CALCULATORS
// ============================================

/**
 * Get the last Monday of May (Memorial Day)
 */
function getLastMondayOfMay(year: number): Date {
  // Start at May 31st and work backwards to find Monday
  const may31 = new Date(year, 4, 31); // Month is 0-indexed
  const dayOfWeek = may31.getDay();
  // If Sunday (0), go back 6 days; if Monday (1), stay; otherwise calculate
  const daysToSubtract = dayOfWeek === 0 ? 6 : (dayOfWeek - 1);
  return new Date(year, 4, 31 - daysToSubtract);
}

/**
 * Get the first Monday of September (Labor Day)
 */
function getFirstMondayOfSeptember(year: number): Date {
  const sept1 = new Date(year, 8, 1); // Month is 0-indexed
  const dayOfWeek = sept1.getDay();
  // If Sunday (0), next day is Monday; if Monday (1), stay; otherwise calculate
  const daysToAdd = dayOfWeek === 0 ? 1 : dayOfWeek === 1 ? 0 : (8 - dayOfWeek);
  return new Date(year, 8, 1 + daysToAdd);
}

/**
 * Get the fourth Thursday of November (Thanksgiving)
 */
function getFourthThursdayOfNovember(year: number): Date {
  const nov1 = new Date(year, 10, 1); // Month is 0-indexed
  const dayOfWeek = nov1.getDay();
  // Calculate days to first Thursday (Thursday = 4)
  const daysToThursday = dayOfWeek <= 4 ? (4 - dayOfWeek) : (11 - dayOfWeek);
  const firstThursday = 1 + daysToThursday;
  // Fourth Thursday is 3 weeks later
  return new Date(year, 10, firstThursday + 21);
}

// ============================================
// HOLIDAY DEFINITIONS
// ============================================

/**
 * Holiday definitions with date calculators and closure rules
 *
 * Closure types:
 * - ALL: All departments closed
 * - PARTIAL_SALES_ONLY: Only New/Used open, all others closed
 */
export const HOLIDAY_DEFINITIONS: HolidayDefinition[] = [
  {
    name: 'MEMORIAL_DAY',
    displayName: 'Memorial Day',
    getDate: getLastMondayOfMay,
    closureType: 'ALL',
  },
  {
    name: 'JULY_4TH',
    displayName: 'Independence Day',
    getDate: (year: number) => new Date(year, 6, 4), // July 4th
    closureType: 'ALL',
  },
  {
    name: 'LABOR_DAY',
    displayName: 'Labor Day',
    getDate: getFirstMondayOfSeptember,
    closureType: 'PARTIAL_SALES_ONLY', // New/Used open, others closed
  },
  {
    name: 'THANKSGIVING',
    displayName: 'Thanksgiving',
    getDate: getFourthThursdayOfNovember,
    closureType: 'ALL',
  },
  {
    name: 'CHRISTMAS',
    displayName: 'Christmas Day',
    getDate: (year: number) => new Date(year, 11, 25), // December 25th
    closureType: 'ALL',
  },
  {
    name: 'NEW_YEARS_DAY',
    displayName: "New Year's Day",
    getDate: (year: number) => new Date(year, 0, 1), // January 1st
    closureType: 'ALL',
  },
];

// ============================================
// LOOKUP FUNCTIONS
// ============================================

/**
 * Get location schedule configuration by location ID
 */
export function getLocationSchedule(locationId: number): LocationScheduleConfig | null {
  return LOCATION_SCHEDULES.find((loc) => loc.locationId === locationId) || null;
}

/**
 * Get Saturday department schedule for a location
 */
export function getSaturdaySchedule(locationId: number): DepartmentSchedule {
  const location = getLocationSchedule(locationId);
  if (!location) {
    // Default to all open if location not found
    return SATURDAY_SCHEDULES.ALL_OPEN;
  }
  // Convert new per-department format to DepartmentSchedule
  const schedule = location.saturdaySchedule;
  return {
    NEW_VEHICLE: schedule.NEW_VEHICLE ? 'OPEN' : 'CLOSED',
    USED_VEHICLE: schedule.USED_VEHICLE ? 'OPEN' : 'CLOSED',
    SERVICE: schedule.SERVICE ? 'OPEN' : 'CLOSED',
    PARTS: schedule.PARTS ? 'OPEN' : 'CLOSED',
    BODY_SHOP: schedule.BODY_SHOP ? 'OPEN' : 'CLOSED',
    RENTAL: schedule.RENTAL ? 'OPEN' : 'CLOSED',
    OTHERS: 'OPEN', // OTHERS always follows weekday schedule
  };
}

/**
 * Get Sunday schedule (always all closed)
 */
export function getSundaySchedule(): DepartmentSchedule {
  return ALL_CLOSED;
}

/**
 * Get weekday schedule (Monday-Friday, always all open)
 */
export function getWeekdaySchedule(): DepartmentSchedule {
  return ALL_OPEN;
}

/**
 * Get resolved holidays for a specific year
 */
export function getHolidaysForYear(year: number): ResolvedHoliday[] {
  return HOLIDAY_DEFINITIONS.map((def) => ({
    name: def.name,
    displayName: def.displayName,
    date: def.getDate(year),
    closureType: def.closureType,
  }));
}

/**
 * Get holidays for a specific month/year
 */
export function getHolidaysInMonth(year: number, month: number): ResolvedHoliday[] {
  const allHolidays = getHolidaysForYear(year);
  return allHolidays.filter((h) => h.date.getMonth() === month - 1); // month is 1-indexed
}

/**
 * Check if a date is a holiday and get its info
 */
export function getHolidayOnDate(date: Date): ResolvedHoliday | null {
  const year = date.getFullYear();
  const holidays = getHolidaysForYear(year);

  return holidays.find((h) =>
    h.date.getFullYear() === date.getFullYear() &&
    h.date.getMonth() === date.getMonth() &&
    h.date.getDate() === date.getDate()
  ) || null;
}

/**
 * Get department schedule for a holiday based on closure type
 */
export function getHolidaySchedule(closureType: HolidayClosureType): DepartmentSchedule {
  return closureType === 'ALL' ? ALL_CLOSED : SALES_ONLY;
}
